<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useConfigurationsStore } from "../../../../stores";
import AppModal from "@/components/ui/AppModal.vue";
import NewAcademicQualification
  from "@/domains/home/services/configurations/views/settings/insolvency/academicQualifications/NewAcademicQualification.vue";
import EditAcademicQualification
  from "@/domains/home/services/configurations/views/settings/insolvency/academicQualifications/EditAcademicQualification.vue";

interface AcademicQualification {
  id: string
  name: string
}

const store = useConfigurationsStore();
const showAddBody = ref(false)
const showEditBody = ref(false)
const qualifications = ref<AcademicQualification[]>([])
const draft = ref<AcademicQualification | null>(null)

const rows = {
  odd: 'border-t border-gray-200 hover:bg-gray-10',
  even: 'border-t border-gray-200 bg-gray-10'
}

const academicQualifications = computed(() => store.academicQualifications);

const openForm = () => {
  showAddBody.value = true
}

const openEditForm = (draftData: AcademicQualification) => {
  draft.value = draftData
  showEditBody.value = true
}

onMounted(() => {
  store.fetchAcademicQualifications();
})

watch(academicQualifications, (data) => {
  if (data?.data) {
    qualifications.value = data.data
  }
})
</script>

<template>
  <div class="flex flex-col">
    <!-- Modals -->
    <AppModal v-model="showAddBody" title="Add Academic Qualification" hide-footer>
      <NewAcademicQualification />
    </AppModal>
    <AppModal v-model="showEditBody" title="Update Academic Qualification" hide-footer>
      <EditAcademicQualification :draft-data="draft" />
    </AppModal>

    <!-- Action Bar -->
    <div class="flex items-center gap-2 justify-end mb-4">
      <button
        @click="openForm"
        class="btn-primary"
      >
        <i class="fa-solid fa-plus"></i>
        <span>Add Qualification</span>
      </button>
    </div>

    <!-- Table -->
    <div class="bg-white rounded border border-gray-200 overflow-hidden flex-1">
      <div class="overflow-x-auto">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">#</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Name</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(pb, idx) in qualifications" :key="pb.id" :class="[(idx % 2) ? rows.even : rows.odd]">
              <td class="py-3 px-4 border-b border-gray-200">{{ idx + 1 }}</td>
              <td class="py-3 px-4 border-b border-gray-200 font-bold text-capitalize">{{ pb.name }}</td>
              <td class="py-3 px-4 border-b border-gray-200">
                <div class="flex items-center justify-center space-x-2">
                  <button
                    @click="openEditForm(pb)"
                    class="btn-sm-primary"
                  >
                    <i class="fa-solid fa-pencil"></i>
                    <span>Edit</span>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="qualifications.length === 0">
              <td colspan="3" class="py-4 text-center text-gray-500">
                No academic qualifications found
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
